import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { TagTypesEnum, UserRoleEnum } from 'enums'
import { ICategory, IDocumentLabel, IFlow, IInvoice, IPagination, IPartner, IScope, ITenantFlow } from 'interfaces'
import { IContact } from 'interfaces/contact.interface'
import { ISubSubscription } from 'interfaces/sub-subscription.interface'
import { ISubscription } from 'interfaces/subscription.interface'
import { ISystem } from 'interfaces/system.interface'
import { ITenant } from 'interfaces/tenant.interface'
import { IUser } from 'interfaces/user.interface'
import { getToken } from 'utils'
import { BASE_URL } from '../constants'

export const reachwareApi = createApi({
  reducerPath: 'reachwareApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers) => {
      headers.set('Authorization', `Bearer ${getToken()}`)
      return headers
    },
  }),
  tagTypes: [
    TagTypesEnum.SYSTEMS,
    TagTypesEnum.CATEGORIES,
    TagTypesEnum.SCOPES,
    TagTypesEnum.TENANTS,
    TagTypesEnum.USERS,
    TagTypesEnum.SUBSCRIPTIONS,
    TagTypesEnum.INVOICES,
    TagTypesEnum.FLOWS,
    TagTypesEnum.CONTACTS,
    TagTypesEnum.PARTNERS,
    TagTypesEnum.SUB_SUBSCRIPTIONS,
    TagTypesEnum.SUBSCRIPTIONS_LOCATIONS,
  ],
  endpoints: (builder) => ({
    setPassword: builder.mutation<void, { token: string; password: string }>({
      query: (setPasswordDto) => ({ url: `/auth/set-password`, method: 'POST', body: setPasswordDto }),
    }),
    forgetPassword: builder.mutation<void, { email: string }>({
      query: (forgetPasswordDto) => ({ url: '/auth/forget-password', method: 'POST', body: forgetPasswordDto }),
    }),
    getScopeById: builder.query<IScope, string | number | undefined>({
      query: (id: string) => `scopes/${id}`,
      providesTags: [TagTypesEnum.SCOPES],
    }),
    getProductBasedOnSystem: builder.query<IPagination<IScope>, any>({
      query: (id: string) => `scopes/system/${id}`,
      providesTags: [TagTypesEnum.SCOPES],
    }),
    getScopes: builder.query<IPagination<IScope>, any>({
      query: (search) => ({
        method: 'POST',
        url: `scopes/search`,
        body: search,
      }),
      providesTags: [TagTypesEnum.SCOPES],
    }),
    createScope: builder.mutation<IScope, Partial<IScope>>({
      query: (scope: IScope) => ({
        url: '/scopes',
        method: 'POST',
        body: scope,
      }),
      invalidatesTags: [TagTypesEnum.SCOPES],
    }),
    updateScope: builder.mutation<void, Partial<IScope>>({
      query: (scope) => ({
        method: 'PATCH',
        url: `/scopes/${scope.id}`,
        body: scope,
      }),
      invalidatesTags: [TagTypesEnum.SYSTEMS],
    }),
    deleteScope: builder.mutation<void, number>({
      query: (id) => ({ method: 'DELETE', url: `scopes/${id}` }),
      invalidatesTags: [TagTypesEnum.SCOPES],
    }),
    createSystem: builder.mutation<ISystem, ISystem>({
      query: (system: Partial<ISystem>) => ({ url: `/solutions`, method: 'POST', body: system }),
      invalidatesTags: [TagTypesEnum.SYSTEMS],
    }),
    getSystems: builder.query<IPagination<ISystem>, any | undefined>({
      query: (searchParams) => ({
        url: `/solutions/search`,
        method: 'POST',
        body: searchParams,
      }),
      providesTags: [TagTypesEnum.SYSTEMS],
    }),
    getSystemsAgreement: builder.query<IPagination<ISystem>, any | undefined>({
      query: (searchParams) => ({
        url: `/solutions/agreement`,
        method: 'POST',
        body: searchParams,
      }),
      providesTags: [TagTypesEnum.SYSTEMS],
    }),
    updateSystem: builder.mutation<
      void,
      {
        systemId: number
        categoryId: number
        name: string
        logo?: string
        description: string
        link: string
        agreement: boolean
        agreement_attachment?: any
      }
    >({
      query: ({ systemId, categoryId, name, logo, description, link, agreement_attachment }) => ({
        method: 'PATCH',
        url: `/solutions/${systemId}`,
        body: { categoryId, name, logo, description, link, agreement_attachment },
      }),
      invalidatesTags: [TagTypesEnum.SYSTEMS],
    }),

    updateSystemAgreement: builder.mutation<void, { systemId: number; agreement: boolean; agreement_date: any }>({
      query: ({ systemId, agreement, agreement_date }) => ({
        method: 'PATCH',
        url: `/solutions/${systemId}`,
        body: { agreement, agreement_date },
      }),
      invalidatesTags: [TagTypesEnum.SYSTEMS],
    }),
    deleteSystem: builder.mutation<void, { systemId: number }>({
      query: ({ systemId }) => ({
        method: 'DELETE',
        url: `/solutions/${systemId}`,
      }),
      invalidatesTags: [TagTypesEnum.SYSTEMS],
    }),
    getCategories: builder.query<IPagination<ICategory>, any>({
      query: (query) => ({ url: `/system-category/search`, method: 'POST', body: query }),
      providesTags: [TagTypesEnum.CATEGORIES],
    }),
    createCategory: builder.mutation<ICategory, Partial<ICategory>>({
      query: (category: Partial<ICategory>) => ({
        url: `/system-category`,
        method: 'POST',
        body: category,
      }),
      invalidatesTags: [TagTypesEnum.CATEGORIES],
    }),
    deleteCategories: builder.mutation<IFlow, number | string>({
      query: (id) => ({ method: 'DELETE', url: `/system-category/${id}` }),
      invalidatesTags: [TagTypesEnum.CATEGORIES],
    }),
    updateCategory: builder.mutation<IUser, Partial<ICategory>>({
      query: (category) => ({
        url: `/system-category/${category.id}`,
        method: 'PATCH',
        body: category,
      }),
      invalidatesTags: [TagTypesEnum.CATEGORIES],
    }),
    createFlow: builder.mutation<IFlow, Partial<IFlow>>({
      query: (flow: IFlow) => ({ method: 'POST', url: `/flows`, body: flow }),
      invalidatesTags: [TagTypesEnum.SCOPES, TagTypesEnum.FLOWS],
    }),
    updateFlow: builder.mutation<IFlow, Partial<IFlow>>({
      query: (flow: IFlow) => ({ method: 'PATCH', url: `/flows/${flow.id}`, body: flow }),
      invalidatesTags: [TagTypesEnum.FLOWS, TagTypesEnum.SCOPES],
    }),
    getFlow: builder.query<IFlow, number | string>({
      query: (id) => ({ method: 'GET', url: `/flows/${id}` }),
      providesTags: [TagTypesEnum.FLOWS],
    }),
    deleteFlow: builder.mutation<IFlow, number | string>({
      query: (id) => ({ method: 'DELETE', url: `/flows/${id}` }),
      invalidatesTags: [TagTypesEnum.SCOPES, TagTypesEnum.FLOWS],
    }),
    createTenant: builder.mutation<ITenant, Partial<ITenant>>({
      query: (tenant) => ({ url: 'tenants', body: tenant, method: 'POST' }),
      invalidatesTags: [TagTypesEnum.TENANTS],
    }),
    getTenants: builder.query<IPagination<ITenant>, any>({
      query: (search) => ({ url: `tenants/search`, method: 'POST', body: search }),
      providesTags: [TagTypesEnum.TENANTS],
    }),
    TenantName: builder.mutation<any, string>({
      query: (name) => ({ url: `tenants/name`, body: { name }, method: 'POST' }),
    }),
    getTenant: builder.query<ITenant, number | string>({
      query: (id) => ({ url: `tenants/${id}`, method: 'GET' }),
      providesTags: [TagTypesEnum.TENANTS],
    }),
    deleteTenant: builder.mutation<void, number | string>({
      query: (id) => ({ url: `tenants/${id}`, method: 'DELETE' }),
      invalidatesTags: [TagTypesEnum.TENANTS],
    }),
    createUser: builder.mutation<IUser, IUser>({
      query: (user) => ({
        url: 'users',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: [TagTypesEnum.USERS],
    }),
    getUsers: builder.query<IPagination<IUser>, any>({
      query: (search) => ({ url: 'users/search', method: 'POST', body: search }),
      providesTags: [TagTypesEnum.USERS],
    }),
    getUser: builder.query<IUser, number | string>({
      query: (id) => ({ url: `users/${id}`, method: 'GET' }),
      providesTags: [TagTypesEnum.USERS],
    }),
    updateUser: builder.mutation<any, IUser>({
      query: (user) => ({ url: `users/${user.id}`, method: 'PATCH', body: user }),
      invalidatesTags: [TagTypesEnum.USERS],
    }),
    deleteUser: builder.mutation<void, number | string>({
      query: (id) => ({ url: `users/${id}`, method: 'DELETE' }),
      invalidatesTags: [TagTypesEnum.USERS],
    }),

    getUsersCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `users/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.USERS],
    }),
    createSubscription: builder.mutation<void, any>({
      query: (subscription) => ({
        url: 'tenant-subscription',
        body: subscription,
        method: 'POST',
      }),
      invalidatesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),
    getSubscriptions: builder.query<IPagination<ISubscription>, any>({
      query: (queryParams) => ({ url: '/tenant-subscription/search', method: 'POST', body: queryParams }),
      providesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),

    geSortByCostSubscriptions: builder.query<IPagination<ISubscription>, any>({
      query: (queryParams) => ({ url: '/tenant-subscription/sort-cost', method: 'POST', body: queryParams }),
      providesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),
    getSubscription: builder.query<ISubscription, number | string>({
      query: (id) => ({ url: `/tenant-subscription/${id}` }),
      providesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),
    deleteSubscription: builder.mutation<void, number | string>({
      query: (id) => ({ url: `/tenant-subscription/${id}`, method: 'DELETE' }),
    }),
    updateSubscription: builder.mutation<void, Partial<ISubscription>>({
      query: (subscription) => ({
        url: `/tenant-subscription/${subscription.id}`,
        method: 'PATCH',
        body: subscription,
      }),
    }),

    createSubSubscription: builder.mutation<void, any>({
      query: (sub_subscription) => ({
        url: 'sub-subscription',
        body: sub_subscription,
        method: 'POST',
      }),
      invalidatesTags: [TagTypesEnum.SUB_SUBSCRIPTIONS],
    }),

    getSubSubscriptions: builder.query<IPagination<ISubSubscription>, any>({
      query: (queryParams) => ({ url: '/sub-subscription/search', method: 'POST', body: queryParams }),
      providesTags: [TagTypesEnum.SUB_SUBSCRIPTIONS],
    }),

    getSubSubscription: builder.query<ISubSubscription, number | string>({
      query: (id) => ({ url: `/sub-subscription/${id}` }),
      providesTags: [TagTypesEnum.SUB_SUBSCRIPTIONS],
    }),

    getSubSubscriptionCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `/sub-subscription/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.SUB_SUBSCRIPTIONS],
    }),

    getActiveSubSubscriptionCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `/sub-subscription/count/active`, method: 'GET' }),
      providesTags: [TagTypesEnum.SUB_SUBSCRIPTIONS],
    }),

    getNonActiveSubSubscriptionCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `/sub-subscription/count/non-active`, method: 'GET' }),
      providesTags: [TagTypesEnum.SUB_SUBSCRIPTIONS],
    }),

    deleteSubSubscription: builder.mutation<void, number | string>({
      query: (id) => ({ url: `/sub-subscription/${id}`, method: 'DELETE' }),
    }),

    updateSubSubscription: builder.mutation<void, Partial<ISubSubscription>>({
      query: (sub_subscription) => ({
        url: `/sub-subscription/${sub_subscription.id}`,
        method: 'PATCH',
        body: sub_subscription,
      }),
    }),

    updateTenant: builder.mutation<ITenant, ITenant>({
      query: (tenant) => ({
        url: `tenants/${tenant.id}`,
        method: 'PATCH',
        body: tenant,
      }),
      invalidatesTags: [TagTypesEnum.TENANTS],
    }),
    updateProfile: builder.mutation<
      void,
      {
        emailVerified: boolean
        firstName: string
        lastName: string
        role: UserRoleEnum
        email: string
        tenantId: number
        phone: string
        address: string
        tenant: ITenant
        tenantContact: boolean
        active: boolean
        password: string
        logo: string
      }
    >({
      query: ({
        emailVerified,
        firstName,
        lastName,
        role,
        email,
        tenantId,
        phone,
        address,
        tenant,
        tenantContact,
        password,
        active,
        logo,
      }) => ({
        url: '/users/update-profile',
        method: 'PATCH',
        body: {
          emailVerified,
          firstName,
          lastName,
          role,
          email,
          tenantId,
          phone,
          address,
          tenant,
          tenantContact,
          password,
          active,
          logo,
        },
      }),
    }),

    updateTenantFlow: builder.mutation<any, Partial<ITenantFlow>>({
      query: (tenantFlow: ITenantFlow) => ({ url: `tenant-flows/${tenantFlow.id}`, method: 'PATCH', body: tenantFlow }),
      invalidatesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),
    getInvoices: builder.query<IPagination<IInvoice>, any>({
      query: (search) => ({ url: 'invoices/search', method: 'POST', body: search }),
      providesTags: [TagTypesEnum.INVOICES],
    }),
    getInvoice: builder.query<IInvoice, string | undefined>({
      query: (id) => ({ url: `invoices/${id}`, method: 'GET' }),
      providesTags: [TagTypesEnum.INVOICES],
    }),
    createInvoice: builder.mutation<any, Partial<IInvoice>>({
      query: (body) => ({ url: '/invoices', method: 'POST', body }),
      invalidatesTags: [TagTypesEnum.INVOICES],
    }),
    updateInvoice: builder.mutation<any, Partial<IInvoice>>({
      query: (body) => ({ url: `/invoices/${body.id}`, method: 'PATCH', body }),
      invalidatesTags: [TagTypesEnum.INVOICES],
    }),

    getInvoicesCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `invoices/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.INVOICES],
    }),

    updateContact: builder.mutation<any, Partial<IContact>>({
      query: (body) => ({ url: `/contacts/${body.id}`, method: 'PATCH', body }),
      invalidatesTags: [TagTypesEnum.CONTACTS],
    }),
    createContact: builder.mutation<IContact, Partial<IContact>>({
      query: (body) => ({ url: `/contacts`, method: 'POST', body }),
      invalidatesTags: [TagTypesEnum.CONTACTS],
    }),
    deleteContact: builder.mutation<any, Partial<IContact>>({
      query: (body) => ({ url: `/contacts/${body.id}`, method: 'DELETE' }),
      invalidatesTags: [TagTypesEnum.CONTACTS],
    }),
    getPartners: builder.query<IPagination<IPartner>, any>({
      query: (search) => ({ url: `partners/search`, method: 'POST', body: search }),
    }),
    getPartner: builder.query<IPartner, number | string>({
      query: (id) => ({ url: `/partners/${id}` }),
      providesTags: [TagTypesEnum.PARTNERS],
    }),

    createPartner: builder.mutation<IPartner, IPartner>({
      query: (partner) => ({
        url: 'partners',
        body: partner,
        method: 'POST',
      }),
      invalidatesTags: [TagTypesEnum.PARTNERS],
    }),
    updatePartner: builder.mutation<void, Partial<IPartner>>({
      query: (partner) => ({
        url: `/partners/${partner.id}`,
        method: 'PATCH',
        body: partner,
      }),
    }),
    checkPartnerEmail: builder.mutation<any, string>({
      query: (email) => ({ url: `partners/check-email`, body: { email }, method: 'POST' }),
    }),
    checkPartnerPhoneNumber: builder.mutation<any, string>({
      query: (PhoneNumber) => ({ url: `partners/checkPhoneNumber`, body: { PhoneNumber }, method: 'POST' }),
    }),
    reinviteUser: builder.mutation<any, number>({
      query: (id) => ({ url: `/users/${id}/reinvite`, method: 'POST' }),
    }),
    checkUserEmail: builder.mutation<any, string>({
      query: (email) => ({ url: `users/check-email`, body: { email }, method: 'POST' }),
    }),
    checkTenantTaxId: builder.mutation<any, string>({
      query: (taxId) => ({ url: `tenants/check-tax-id/${taxId}`, method: 'GET' }),
    }),
    checkUserPhoneNumber: builder.mutation<any, string>({
      query: (phone) => ({ url: `users/checkPhoneNumber`, body: { phone }, method: 'POST' }),
    }),
    getSubscriptionWillSuspendThisMonth: builder.query<{ count: number }, void>({
      query: () => ({ url: `tenant-subscription/suspension/subscriptions/month`, method: 'GET' }),
      providesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),
    getActiveSubscriptionCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `tenant-subscription/active/subscriptions`, method: 'GET' }),
      providesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),

    getNonActiveSubscriptionCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `tenant-subscription/non-active/subscriptions`, method: 'GET' }),
      providesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),
    getSubscriptionCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `tenant-subscription/subscriptions/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.SUBSCRIPTIONS],
    }),
    getSubscriptionLocationCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `tenant-subscription/subscriptionsLocation/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.SUBSCRIPTIONS_LOCATIONS],
    }),

    getActiveScopesCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `scopes/active/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.SCOPES],
    }),

    getScopesCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `scopes/scope/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.SCOPES],
    }),

    getActiveTenantsCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `tenants/active/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.TENANTS],
    }),

    getNonActiveTenantsCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `tenants/non-active/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.TENANTS],
    }),

    getTenantsCount: builder.query<{ count: number }, void>({
      query: () => ({ url: `tenants/count`, method: 'GET' }),
      providesTags: [TagTypesEnum.TENANTS],
    }),
    getDocumentLabels: builder.query<Array<IDocumentLabel>, void>({
      query: () => ({ url: `document-labels`, method: 'GET' }),
    }),
  }),
})

export const {
  useGetScopeByIdQuery,
  useGetScopesQuery,
  useCreateScopeMutation,
  useDeleteScopeMutation,
  useGetSystemsQuery,
  useUpdateSystemMutation,
  useDeleteSystemMutation,
  useCreateSystemMutation,
  useCreateCategoryMutation,
  useGetCategoriesQuery,
  useCreateFlowMutation,
  useCreateTenantMutation,
  useGetTenantsQuery,
  useCreateUserMutation,
  useGetUsersQuery,
  useDeleteUserMutation,
  useGetUserQuery,
  useUpdateUserMutation,
  useGetUsersCountQuery,
  useDeleteTenantMutation,
  useGetTenantQuery,
  useSetPasswordMutation,
  useForgetPasswordMutation,
  useCreateSubscriptionMutation,
  useGetSubscriptionsQuery,
  useGetSubscriptionQuery,
  useDeleteSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useGeSortByCostSubscriptionsQuery,
  useUpdateTenantMutation,
  useUpdateProfileMutation,
  useUpdateTenantFlowMutation,
  useDeleteFlowMutation,
  useDeleteCategoriesMutation,
  useUpdateCategoryMutation,
  useGetInvoicesQuery,
  useCreateInvoiceMutation,
  useGetInvoiceQuery,
  useUpdateInvoiceMutation,
  useUpdateScopeMutation,
  useGetFlowQuery,
  useUpdateFlowMutation,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useGetPartnersQuery,
  useReinviteUserMutation,
  useCheckUserEmailMutation,
  useGetActiveScopesCountQuery,
  useGetScopesCountQuery,
  useGetActiveTenantsCountQuery,
  useGetTenantsCountQuery,
  useGetActiveSubscriptionCountQuery,
  useGetSubscriptionWillSuspendThisMonthQuery,
  useGetSubscriptionCountQuery,
  useGetSubscriptionLocationCountQuery,
  useCheckUserPhoneNumberMutation,
  useCheckTenantTaxIdMutation,
  useGetDocumentLabelsQuery,
  useGetNonActiveSubscriptionCountQuery,
  useGetNonActiveTenantsCountQuery,
  useGetInvoicesCountQuery,
  useTenantNameMutation,
  useGetProductBasedOnSystemQuery,
  useGetSystemsAgreementQuery,
  useUpdateSystemAgreementMutation,
  useGetPartnerQuery,
  useCreatePartnerMutation,
  useUpdatePartnerMutation,
  useCheckPartnerEmailMutation,
  useCheckPartnerPhoneNumberMutation,
  useCreateSubSubscriptionMutation,
  useGetSubSubscriptionQuery,
  useDeleteSubSubscriptionMutation,
  useUpdateSubSubscriptionMutation,
  useGetSubSubscriptionsQuery,
  useGetSubSubscriptionCountQuery,
  useGetActiveSubSubscriptionCountQuery,
  useGetNonActiveSubSubscriptionCountQuery,
} = reachwareApi
